import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Download, Send, Briefcase } from "lucide-react";

const TYPES = [["all", "Alle"], ["coaching", "Coaching (AVGS)"], ["weiterbildung", "Weiterbildung"]];
const ANSWER = { yes: ["Job gefunden", "bg-emerald-100 text-emerald-800"], training: ["Weiterbildung/Ausbildung", "bg-blue-100 text-blue-800"], no: ["Nein, noch nichts", "bg-rose-100 text-rose-800"] };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

function Stat({ label, value, sub, testId, accent }) {
  return (
    <div className={`border bg-white p-5 ${accent || "border-slate-200"}`} data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{label}</div>
      <div className="font-heading text-3xl font-light tracking-tighter text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Bar({ yes, training, no, none }) {
  const t = yes + training + no + none || 1;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
      <div className="bg-emerald-500" style={{ width: `${(yes / t) * 100}%` }} />
      <div className="bg-blue-500" style={{ width: `${(training / t) * 100}%` }} />
      <div className="bg-rose-400" style={{ width: `${(no / t) * 100}%` }} />
      <div className="bg-slate-300" style={{ width: `${(none / t) * 100}%` }} />
    </div>
  );
}

function StageTable({ rows, first = "Stufe" }) {
  return (
    <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="placement-stage-table">
      <div className="min-w-[720px]">
      <div className="grid grid-cols-9 gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-overline text-slate-500 border-b">
        <div>{first}</div><div>Gesendet</div><div>Geantwortet</div><div>Job</div><div>Weiterb.</div><div>Nein</div><div>Keine Antw.</div><div>Antwortquote</div><div>Job + WB</div>
      </div>
      {rows.map((r) => (
        <div key={r.stage ?? r.person_type} className="px-4 py-3 border-b last:border-0 text-sm" data-testid={`placement-row-${r.stage ?? r.person_type}`}>
          <div className="grid grid-cols-9 gap-2 mb-2">
            <div className="font-medium">{r.stage ? `${r.stage} Monate` : r.label}</div>
            <div>{r.sent}</div><div>{r.answered}</div><div className="text-emerald-700">{r.yes}</div><div className="text-blue-700">{r.training}</div>
            <div className="text-rose-700">{r.no}</div><div className="text-slate-500">{r.no_reply}</div>
            <div>{r.answer_rate}%</div><div>{r.positive_rate_of_answered}%</div>
          </div>
          <Bar yes={r.yes} training={r.training} no={r.no} none={r.no_reply} />
        </div>
      ))}
      </div>
    </div>
  );
}

function SurveyList({ rows, onSet }) {
  if (!rows.length) return <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="placement-empty">Noch keine Abfragen verschickt.</div>;
  return (
    <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="placement-survey-list">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-overline text-slate-500 border-b">
          <tr>{["Name", "Bereich", "Maßnahmeende", "Stufe", "Gesendet", "Antwort", "Beantwortet", "Manuell"].map((h) => <th key={h} className="text-left px-3 py-2 font-bold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.survey_id} className="border-b last:border-0" data-testid={`placement-survey-${s.survey_id}`}>
              <td className="px-3 py-2 font-medium">{s.name}</td>
              <td className="px-3 py-2">{s.person_type === "coaching" ? "Coaching" : "Weiterbildung"}</td>
              <td className="px-3 py-2">{fmt(s.end_date)}</td>
              <td className="px-3 py-2">{s.stage} Mon.</td>
              <td className="px-3 py-2">{fmt(s.sent_at)}{s.send_status === "failed" && <span className="ml-1 text-rose-600">(Fehler)</span>}</td>
              <td className="px-3 py-2">
                {ANSWER[s.answer] ? <span className={`px-2 py-0.5 rounded-full text-xs ${ANSWER[s.answer][1]}`}>{ANSWER[s.answer][0]}</span> : <span className="text-xs text-slate-400">keine Antwort</span>}
              </td>
              <td className="px-3 py-2 text-xs text-slate-500">{fmt(s.answered_at)}{s.answer_source && s.answer_source !== "email" ? ` · ${s.answer_source}` : ""}</td>
              <td className="px-3 py-2">
                <select className="border border-slate-200 text-xs px-2 py-1 bg-white" value={s.answer || ""} onChange={(e) => onSet(s.survey_id, e.target.value || null)} data-testid={`placement-manual-${s.survey_id}`}>
                  <option value="">—</option><option value="yes">Job gefunden</option><option value="training">Weiterbildung/Ausbildung</option><option value="no">Nein</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PlacementSurveyPage() {
  const [type, setType] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/placement/stats", { params: { person_type: type } }).then((r) => setData(r.data)).catch(err);
  useEffect(() => { load(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/placement/run", null, { timeout: 120000 });
      toast.success(r.skipped ? "Versand läuft bereits" : `${r.sent} Abfrage(n) verschickt${r.skipped_no_email ? ` · ${r.skipped_no_email} ohne E-Mail-Adresse` : ""}`);
      load();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  const setAnswer = (id, answer) => api.put(`/placement/${id}/answer`, { answer }).then(() => { toast.success("Antwort gespeichert"); load(); }).catch(err);
  const exportCsv = async () => {
    try {
      const r = await api.get("/placement/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = "verbleibsquote_azav.csv";
      a.click();
    } catch (e) { err(e); }
  };

  if (!data) return <div className="p-8 text-xs text-slate-400">Lädt…</div>;
  const t = data.totals, p = data.persons;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-8 space-y-8" data-testid="placement-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1"><Briefcase size={12} /> AZAV-Nachweis</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900">Verbleibsquote</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">Automatische Abfrage „Job gefunden / In Weiterbildung bzw. Ausbildung / Nein“ 3, 6, 9 und 12 Monate nach Maßnahmeende. Nach „Job“ oder „Weiterbildung“ wird nicht weiter gefragt.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2 text-sm hover:bg-slate-50 transition-colors" data-testid="placement-export-btn"><Download size={14} /> CSV-Export</button>
          <button onClick={runNow} disabled={busy} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700 transition-colors disabled:opacity-50" data-testid="placement-run-btn"><Send size={14} /> {busy ? "Prüfe…" : "Fällige jetzt senden"}</button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="placement-type-filter">
        {TYPES.map(([k, l]) => (
          <button key={k} onClick={() => setType(k)} className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${type === k ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`} data-testid={`placement-filter-${k}`}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat testId="placement-stat-combined" label="Verbleibsquote gesamt (Job + Weiterbildung)" value={`${p.combined_rate}%`} sub={`${p.found_job + p.in_training} von ${p.asked} Personen · ${p.combined_rate_of_responded}% der Antwortenden`} accent="border-slate-900" />
        <Stat testId="placement-stat-rate" label="Job gefunden" value={`${p.placement_rate}%`} sub={`${p.found_job} ${p.found_job === 1 ? "Person" : "Personen"} · ${p.placement_rate_of_responded}% der Antwortenden`} accent="border-emerald-300" />
        <Stat testId="placement-stat-training" label="In Weiterbildung / Ausbildung" value={`${p.training_rate}%`} sub={`${p.in_training} ${p.in_training === 1 ? "Person" : "Personen"}`} accent="border-blue-300" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat testId="placement-stat-sent" label="Abfragen gesendet" value={t.sent} sub={`an ${p.asked} ${p.asked === 1 ? "Person" : "Personen"}`} />
        <Stat testId="placement-stat-answered" label="Geantwortet" value={`${t.answer_rate}%`} sub={`${t.answered} Antworten · ${t.yes} Job / ${t.training} WB / ${t.no} Nein`} />
        <Stat testId="placement-stat-noreply" label="Keine Antwort" value={t.no_reply} sub={`${p.never_responded} ${p.never_responded === 1 ? "Person" : "Personen"} nie geantwortet`} />
      </div>

      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-medium text-slate-900">Nach Abfrage-Stufe</h2>
        <StageTable rows={data.by_stage} />
      </section>
      {type === "all" && (
        <section className="space-y-3">
          <h2 className="text-base md:text-lg font-medium text-slate-900">Nach Bereich</h2>
          <StageTable first="Bereich" rows={data.by_type.map((r) => ({ ...r, label: r.person_type === "coaching" ? "Coaching" : "Weiterbildung" }))} />
        </section>
      )}
      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-medium text-slate-900">Alle Abfragen</h2>
        <SurveyList rows={data.surveys} onSet={setAnswer} />
      </section>
    </div>
  );
}
